/**
 * This file should be kept identical in both AdminUI and AWS-Stack.
 *
 * In AWs it is used to calculate the volume and in AdminUI it is used to
 * calculate volumes for checking corrections, is attempt to keep results
 * consistent and reduce duplication of code
 */
export type TankHeightVolume = {
	height: number;
	volume: number;
	maxVolume: number;
	minVolume: number;
};

export abstract class FuelTankVolumeCalculator {
	public static async calculate(
		capacitance: number,
		formula: string,
		formulaName: string,
		tankId: number,
		parameters: Record<string, number | string>,
		correction: Record<string, number | null>,
		probeLength: number,
		emptyCapacitance: number,
		fluidDielectric: number,
		dielectric: number,
	): Promise<TankHeightVolume | Error> {
		const volumeResult = await this.calculateFuelTankVolume(
			capacitance,
			formula,
			formulaName,
			tankId,
			parameters,
			correction,
			probeLength,
			emptyCapacitance,
			fluidDielectric,
			dielectric,
		);

		//calculate minimum measurable volume for tank
		const minResult = await this.calculateFuelTankVolume(
			emptyCapacitance, //to calc minimum, pass emptyCapacitance as capacitance
			formula,
			formulaName,
			tankId,
			parameters,
			{}, //no corrections
			probeLength,
			emptyCapacitance,
			fluidDielectric,
			dielectric,
		);

		if (volumeResult instanceof Error) {
			return volumeResult;
		}

		/*Two tank formulas 'Inclined Cylinder - Airfuels' and 'Stretched Cylinder' have formulas that will return
      an invalid min volume result, when this happens default min to 0*/
		if (!(minResult instanceof Error)) {
			volumeResult.minVolume = minResult.volume;
		}

		if (volumeResult.minVolume >= volumeResult.maxVolume) {
			return Error(
				`Invalid volume calculation - calculated min volume is not less than max volume for formula:${formulaName} on tankId:${tankId}.
      ${JSON.stringify(volumeResult)}`,
			);
		}

		//result can be above max volume or below it
		if (volumeResult.volume > volumeResult.maxVolume) {
			volumeResult.volume = volumeResult.maxVolume;
		}
		if (volumeResult.volume < volumeResult.minVolume) {
			volumeResult.volume = volumeResult.minVolume;
		}

		return volumeResult;
	}

	static ullage(volume: number | null, nominalVolume: number | null, safeFill: number | null) {
		if (volume === null) {
			return null;
		}
		if (nominalVolume === null && safeFill === null) {
			return null;
		}
		return Math.max(0, Math.min(nominalVolume ?? Infinity, safeFill ?? Infinity) - volume);
	}

	static async calculateFuelTankVolume(
		capacitance: number,
		formula: string,
		formulaName: string,
		tankId: number,
		parameters: Record<string, number | string>,
		correction: Record<string, number | null>,
		probeLength: number,
		emptyCapacitance: number,
		fluidDielectric: number,
		dielectric: number,
	): Promise<TankHeightVolume | Error> {
		const additionalParams = {
			reading: capacitance,
			probeLength,
			emptyCapacitance,
			fluidDielectric,
			dielectric,
		};

		const appendedParams = this.appendParametersWithCorrections(parameters, correction);
		const fullParams = { ...additionalParams, ...appendedParams };
		const formulaWithParams = this.addParamsToFormula(fullParams, formula);

		return this.evaluateFormula(formulaWithParams, formulaName, tankId);
	}

	private static async evaluateFormula(
		formula: string,
		formulaName: string,
		tankId: number,
	): Promise<TankHeightVolume | Error> {
		let volume, Hm, maxVolume;
		try {
			await eval(formula);
		} catch (err) {
			return new Error(`Error calculating formula for tankId:${tankId}: ${err.message}.
    Formula =
    ${formula}`);
		}

		if (
			Hm != undefined &&
			Number.isFinite(+Hm) &&
			volume != undefined &&
			Number.isFinite(+volume) &&
			maxVolume != undefined &&
			Number.isFinite(+maxVolume)
		) {
			return {
				height: Math.round(Hm),
				volume: Math.round(volume),
				maxVolume: Math.round(maxVolume),
				minVolume: 0,
			};
		} else {
			return new Error(
				`Formula ${formulaName} did not generate valid outputs for height, volume and maxVolume.
       height=${Hm}, volume=${volume}, maxVolume:${maxVolume} for tankId ${tankId}
       Formula =
       ${formula}`,
			);
		}
	}

	/**
	 * Creates the full formula to be evaluates with the params unique to
	 * tank and the formula stored in the database
	 *
	 */
	private static addParamsToFormula(params: Record<string, unknown>, formula: string): string {
		//init all standard vars
		let fnStart = 'var accuracyOffset, dimension\n';
		//set vars for function to be set at top of file
		for (const [key, value] of Object.entries(params)) {
			if (value === undefined) {
				continue;
			}
			if (typeof value === 'number') {
				fnStart += `var ${key} = ${value}\n`;
			} else {
				fnStart += `var ${key} = "${value}"\n`;
			}
		}

		return fnStart + '\n' + formula;
	}

	/**
	 * Corrections are modifications to the parameters, where a correction exists
	 * for a paramter add the correction value to the parameter value if number
	 *
	 */
	public static appendParametersWithCorrections(
		parameters: Record<string, number | string>,
		correction: Record<string, number | string | null>,
	): Record<string, number | string> {
		const appendedParams = JSON.parse(JSON.stringify(parameters));
		const keys = Object.keys(appendedParams);

		for (const key of keys) {
			let paramVal = appendedParams[key];
			if (paramVal !== null && !isNaN(+paramVal)) {
				paramVal = +paramVal;

				const correctionVal = correction[key];
				if (correctionVal !== null && !isNaN(+correctionVal)) {
					appendedParams[key] = paramVal + +correctionVal;
				}
			}
		}

		return appendedParams;
	}
}
